import { FixedAsset, PaymentTaskFixedAsset } from '@/hooks/api/models';
import { useLoading } from '@/providers/LoadingProvider';
import { useCurrentTeamMember } from '@/providers/TeamMemberProvider';
import { useCurrentPaymentTask } from '@/providers/PaymentTaskProvider';
import { useCreatePaymentTaskFixedAsset } from '@/hooks/api/1160_create_payment_task_fixed_asset';
import { usePaymentTaskFixedAssetsInvalidate } from '@/hooks/api/1150_payment_task_fixed_assets';
import React, { useEffect, useState } from 'react';
import { SelectOptionType } from '@/components/element/Form/SelectOptionType';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { toastApiError } from '@/lib/toast';
import { SingleValue } from 'react-select';
import { SelectSingle } from '@/components/element/Form/SelectSingle';
import { FormGroup } from '@/components/element/Form/FormGroup';
import { useParams } from 'react-router-dom';
import { InputPrice } from '@/components/element/Form/InputPrice';

type FormInputs = {
  identifier: string;
  team_id: number;
  purchase_order_id: number;
  task_id: number;
  fixed_asset_id: number;
  amount: number;
};

export const PaymentTaskFixedAssetCreateForm = ({
  fixedAssets,
  paymentTaskFixedAssets,
}: {
  fixedAssets: FixedAsset[];
  paymentTaskFixedAssets: PaymentTaskFixedAsset[];
}) => {
  const { setShowLoading } = useLoading();
  const { _w_, _t_, me } = useCurrentTeamMember();
  const { purchase_order_id } = useParams();
  const { paymentTask: task } = useCurrentPaymentTask();
  const { mutate, data, isPending, error } = useCreatePaymentTaskFixedAsset();
  const { invalidate } = usePaymentTaskFixedAssetsInvalidate({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    purchase_order_id: Number(purchase_order_id),
    task_id: task!.id,
  });
  const [options, setOptions] = useState<SelectOptionType[]>([]);
  const {
    control,
    setValue,
    getValues,
    reset,
    trigger,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormInputs>({
    mode: 'onSubmit',
    criteriaMode: 'all',
    defaultValues: {
      identifier: _w_!.identifier,
      team_id: _t_!.id,
      purchase_order_id: Number(purchase_order_id),
      task_id: task!.id,
    },
  });

  useEffect(() => {
    if (fixedAssets)
      setOptions(
        fixedAssets.map((d): SelectOptionType => {
          if (d.fixed_asset_status.name == 'preparing') return { value: d.id, label: `【構築中】${d.name}` };

          if (d.fixed_asset_status.name == 'started') return { value: d.id, label: `【使用開始済】${d.name}` };

          return { value: d.id, label: `${d.name}` };
        })
      );
  }, [fixedAssets]);

  useEffect(() => setShowLoading(isPending), [isPending]);
  useEffect(() => toastApiError(error), [error]);

  useEffect(() => {
    if (data) {
      invalidate();
    }
  }, [data]);

  const onSubmit: SubmitHandler<FormInputs> = (data) => {
    setShowLoading(true);
    mutate(data);
  };

  const onFixedAssetChange = (sv: SingleValue<SelectOptionType>) => {
    if (sv) {
      setValue('fixed_asset_id', sv.value);
      trigger('fixed_asset_id');
    } else {
      reset(undefined, { keepDefaultValues: true });
    }
  };

  return (
    <>
      <div className="col-12 col-md-6">
        <div className="form-group">
          <label>
            固定資産<span className="text-danger">*</span>
          </label>
          <Controller
            control={control}
            name="fixed_asset_id"
            rules={{ required: true }}
            render={({ field: { ref, ...field } }) => (
              <SelectSingle
                options={options}
                selectedId={getValues('fixed_asset_id')}
                onChange={onFixedAssetChange}
                error={errors.fixed_asset_id != null}
              />
            )}
          />
        </div>
      </div>
      <div className="col-12 col-md-3">
        <FormGroup label="金額" required={true}>
          <div className="input-group">
            <Controller
              render={({ field: { onChange, value, ...field } }) => (
                <InputPrice onChange={onChange} value={value} error={errors.amount} />
              )}
              control={control}
              name="amount"
              rules={{ required: true }}
            />
          </div>
        </FormGroup>
      </div>
      <div className="col-12 col-md-3">
        <div className="form-group">
          <label>&nbsp;</label>
          <button className="btn w-100 btn-icon bg-gradient-dark" onClick={() => handleSubmit(onSubmit)()}>
            <i className="fa fa-plus"></i>追加
          </button>
        </div>
      </div>
    </>
  );
};
